import { useState, useEffect } from 'react';
import {
  AppPageHeader,
  ColumnsType,
  CompaniesInfiniteSelectField,
  DeleteIcon,
  EditIcon,
  Group,
  InfiniteTable,
  InputText,
  SearchFileIcon,
} from '@/components/_common';
import { Col, Modal, Popover, Row, Space, Tooltip, notification } from 'antd';
import { CheckSquare, Info, XSquare } from 'react-feather';
import { useDisclosure } from '@/hooks';
import EyeIcon from '@/components/_common/EyeIcon';
import {
  BilletsStatus,
  BilletsStatusColor,
  BilletsStatusType,
  ListBillets,
  ListBilletsQuery,
} from '@/core/domain/financial/Boletos/Boletos.types';
import {
  useBulletsList,
  useDeleteBankSlip,
  useRemitanceReturnProtocol,
} from '@/core/domain/financial/Boletos/Boletos.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import StatusTag from '@/components/_common/StatusTag';
import BankSlipViewModal from '../BankSlipViewModal';
import moment from 'moment';
import BankSlipEditModal from '../BankSlipEditModal';
import BankSlipDownload from '../../BankSlipDownload';
import BankSlipWhatsApp from '../../BankSlipWhatsApp';

interface Props {
  selectedBankSlips: ListBillets[];
  setSelectedBankSlips: React.Dispatch<React.SetStateAction<ListBillets[]>>;
  setQueryParams: React.Dispatch<React.SetStateAction<ListBilletsQuery>>;
  queryParams: ListBilletsQuery;
}

export default function BankSlipHistoryTable({
  selectedBankSlips,
  setSelectedBankSlips,
  setQueryParams,
  queryParams,
}: Props) {
  const [selectedBankSlip, setSelectedBankSlip] = useState<ListBillets>();
  const [editBankSlip, setEditBankSlip] = useState<ListBillets>();
  const [viewBankSlip, viewBankSlipAction] = useDisclosure();

  const { data, fetchNextPage, hasNextPage, isFetching } =
    useBulletsList(queryParams);
  const bullets = flatInfiniteData(data);
  const removeMutation = useDeleteBankSlip();
  const verifySituationMutation = useRemitanceReturnProtocol();

  const handleRemoveClick = (bankSlip: ListBillets) => {
    Modal.confirm({
      title: 'Exclusão',
      content: `Deseja realmente excluir o boleto, esse processo será irreversível`,
      onOk: async () => {
        await removeMutation.mutateAsync({
          companyId: Number(bankSlip.companyId),
          installmentReceivableBillsIds: [bankSlip.id],
        });
      },
      okType: 'danger',
    });
  };

  const handleVerifySituation = async (bankSlip: ListBillets) => {
    if (bankSlip) {
      await verifySituationMutation.mutateAsync({
        companyId: Number(bankSlip.companyId),
        protocol: bankSlip.protocol,
      });
    }
  };

  useEffect(() => {
    notification.info({
      message: 'Atenção!',
      description:
        'Para listar os boletos, necessário informar uma empresa nos filtros',
      duration: 6,
    });
  }, []);

  const bankSlipColumns: ColumnsType<ListBillets> = [
    { title: 'Empresa', dataIndex: 'companyName', width: 350, ellipsis: true },
    {
      title: 'Banco',
      dataIndex: 'bankAccountName',
      width: 200,
      ellipsis: true,
    },
    { title: 'Devedor', dataIndex: 'debtor', width: 200, ellipsis: true },
    {
      title: 'Cód. Parcela',
      dataIndex: 'codeIdentifier',
      width: 100,
      render: (value) => value ?? '-',
      ellipsis: true,
    },
    {
      title: 'Nosso número',
      dataIndex: 'ourNumber',
      width: 150,
      render: (_, record) =>
        record.ourNumber?.toString().length > 8
          ? record.ourNumber
              ?.toString()
              .replace(
                record.ourNumber?.toString()[1],
                record.ourNumber?.toString()[1] + '-',
              )
          : record.ourNumber,
    },
    {
      title: 'Nº documento',
      dataIndex: 'documentNumber',
      width: 200,
    },
    {
      title: 'Situação',
      dataIndex: 'status',
      width: 120,
      render: (value: BilletsStatus) => (
        <Group gap={10}>
          <StatusTag
            color={BilletsStatusColor[value]}
            label={BilletsStatusType[value]}
          />

          {[BilletsStatus.emited, BilletsStatus.registred].includes(value) && (
            <Tooltip title="Seu boleto será liquidado conforme a regra de processamento e liquidação do seu banco, que geralmente é de (Dia atual + 1). No entanto, podem ocorrer variações dependendo do banco com o qual você trabalha.">
              <Info size={20} cursor="pointer" />
            </Tooltip>
          )}
        </Group>
      ),
    },
    {
      title: 'Data Emissão',
      dataIndex: 'createdAt',
      width: 100,
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Data Venc.',
      dataIndex: 'dueDate',
      width: 100,
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Gerado Remessa',
      dataIndex: 'remittanceEmitted',
      align: 'center',
      width: 150,
      render: (_, record) =>
        record.remittanceEmitted ? (
          <CheckSquare color="green" />
        ) : (
          <XSquare color="red" />
        ),
    },
    {
      align: 'right',
      fixed: 'right',
      render: (_, row) => (
        <Space direction="horizontal">
          <BankSlipWhatsApp
            bankSlip={row}
            show={
              row.status === BilletsStatus.emited ||
              row.status === BilletsStatus.registred ||
              row.status === BilletsStatus.liquidated
            }
          />

          <EyeIcon
            iconProps={{
              onClick: () => {
                viewBankSlipAction.open();
                setSelectedBankSlip(row);
              },
            }}
          />

          <BankSlipDownload
            bankSlip={row}
            show={
              row.status === BilletsStatus.emited ||
              row.status === BilletsStatus.registred ||
              row.status === BilletsStatus.liquidated
            }
          />

          {(row.status === BilletsStatus.saved ||
            row.status === BilletsStatus.emited ||
            row.status === BilletsStatus.failed ||
            row.status === BilletsStatus.rejected) && (
            <>
              <DeleteIcon
                iconProps={{ onClick: () => handleRemoveClick(row) }}
              />
            </>
          )}

          {row.protocol && (
            <SearchFileIcon
              popoverContent={'Visualizar situação do arquivo de remessa'}
              placement="left"
              iconProps={{ onClick: () => handleVerifySituation(row) }}
            />
          )}

          {row.status === BilletsStatus.registred &&
            row.bankAccountCode === '341' &&
            !row?.changeTicketProtocol && (
              <EditIcon iconProps={{ onClick: () => setEditBankSlip(row) }} />
            )}

          {!!row?.changeTicketProtocol && row.bankAccountCode === '341' && (
            <Popover
              title="Alteração em andamento"
              content={
                <div style={{ maxWidth: 400 }}>
                  Uma solicitação de alteração já foi enviada e está sendo
                  processada pelo banco. Por favor, aguarde a atualização antes
                  de realizar uma nova solicitação.
                </div>
              }
            >
              <Info size={20} />
            </Popover>
          )}
        </Space>
      ),
    },
  ];

  const handleSelectBankSlip = (selectedRowKeys: ListBillets[]) => {
    setSelectedBankSlips(selectedRowKeys);
  };

  return (
    <>
      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        searchValue={queryParams.search}
        extraFilters={
          <Row gutter={[12, 12]}>
            <Col span={12}>
              <CompaniesInfiniteSelectField
                onChange={(value) =>
                  setQueryParams((prev) => ({ ...prev, companyId: value }))
                }
                value={queryParams.companyId}
                placeholder="Empresa"
              />
            </Col>

            <Col span={12}>
              <InputText
                onChange={({ target }) =>
                  setQueryParams((prev) => ({
                    ...prev,
                    workOrderNumber: target.value,
                  }))
                }
                value={queryParams.workOrderNumber}
                placeholder="Número da O.S"
                onlyNumber
              />
            </Col>
          </Row>
        }
      />

      <InfiniteTable<ListBillets>
        loading={isFetching}
        dataSource={bullets}
        columns={bankSlipColumns}
        rowKey={(row) => row.id}
        scroll={{ x: true }}
        rowSelection={{
          getCheckboxProps: (item) => ({
            disabled:
              item.status === BilletsStatus.saved ||
              item.status === BilletsStatus.failed ||
              item.status === BilletsStatus.pending_retry ||
              item.status === BilletsStatus.rejected ||
              item.status === BilletsStatus.liquidated ||
              item.status === BilletsStatus.downloaded,
          }),
          selectedRowKeys: selectedBankSlips.map((bankSlip) => bankSlip.id),
          onChange: (_, rows) => handleSelectBankSlip(rows),
        }}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
      />

      <BankSlipViewModal
        onCancel={viewBankSlipAction.close}
        open={viewBankSlip}
        selectedBankSlip={selectedBankSlip}
      />

      <BankSlipEditModal
        open={!!editBankSlip}
        onCancel={() => setEditBankSlip(undefined)}
        data={editBankSlip}
      />
    </>
  );
}
